import React, { useEffect } from 'react';

import classNames from 'classnames';
import { Snackbar as MuiSnackbar } from '@mui/material';

import { Portal } from '../Portal';

import { useStyles } from './Snackbar.styles';

export interface SnackbarProps {
  open: boolean;
  message?: React.ReactNode | React.ReactNode[];
  action: React.ReactNode | React.ReactNode[];
  variant?: 'static' | 'sticky';
  className?: string;
  zPosition?: 'top' | 'bottom';
  hideMessage?: boolean;
}

/**
 * Компонент Snackbar для отображения сообщений и действий.
 * Может быть позиционирован статически или с прилипанием и размещён в верхней или нижней части вьюпорта.
 *
 * @param {SnackbarProps} props - Свойства компонента Snackbar.
 * @param {boolean} props.open - Указывает, открыт ли Snackbar.
 * @param {React.ReactNode | React.ReactNode[]} props.message - Сообщение для отображения в Snackbar.
 * @param {React.ReactNode | React.ReactNode[]} props.action - Действие для отображения в Snackbar (например, кнопка).
 * @param {'static' | 'sticky'} [props.variant='static'] - Вариант Snackbar, влияющий на его стиль отображения.
 * @param {string} [props.className] - Дополнительное имя класса CSS для применения к Snackbar.
 * @param {'top' | 'bottom'} [props.zPosition='top'] - Вертикальное положение Snackbar в вьюпорте.
 * @returns {JSX.Element} - Отрендеренный компонент Snackbar.
 */

export const Snackbar = ({
  open,
  message,
  action,
  className,
  variant = 'static',
  zPosition = 'top',
  hideMessage,
}: SnackbarProps) => {
  const { classes } = useStyles();

  useEffect(() => {
    const webimButtonCorner = Array.from(
      document.getElementsByClassName('webim-button-corner'),
    )[0];

    if (webimButtonCorner) {
      if (open) {
        webimButtonCorner.classList.add('webim-button-corner-button');
      } else {
        webimButtonCorner.classList.remove('webim-button-corner-button');
      }
    }

    const webimChat = document.getElementById('webim_chat');
    if (webimChat) {
      if (open) {
        webimChat.classList.add('webim-chat-button');
      } else {
        webimChat.classList.remove('webim-chat-button');
      }
    }

    return () => {
      if (webimButtonCorner) {
        webimButtonCorner.classList.remove('webim-button-corner-button');
      }

      if (webimChat) {
        webimChat.classList.remove('webim-chat-button');
      }
    };
  }, [open]);

  let contentClassName = classes.snackbarContent;

  if (variant === 'sticky') {
    contentClassName = classes.snackbarContentSticky;
  }

  if (zPosition === 'top') {
    contentClassName = classes.snackbarContentPositionTop;
  }

  return (
    <Portal>
      <MuiSnackbar
        anchorOrigin={{ vertical: zPosition, horizontal: 'center' }}
        className={classNames(classes.snackbar, 'aqa_snackbar', className, {
          [classes.snackbarHideMessage]: !message || hideMessage,
          [classes.snackbarZPositionBottomSticky]:
            zPosition === 'bottom' && variant === 'sticky',
        })}
        open={open}
        message={message}
        action={action}
        ContentProps={{
          className: contentClassName,
        }}
      />
    </Portal>
  );
};
